'use client';

import { create } from 'zustand';

export type ConnectionStatus = 'online' | 'reconnecting' | 'offline';

interface ConnectionState {
  status: ConnectionStatus;
  lastReconnectAt: number;
  // Active socket.io connection id. Threaded through HTTP requests as
  // `X-Socket-Id` so the BE can skip *this* tab when it emits the
  // resulting event — avoids processing the change twice on the
  // originating device.
  socketId: string | null;
  setStatus: (status: ConnectionStatus) => void;
  bumpReconnect: () => void;
  setSocketId: (id: string | null) => void;
}

export const useConnection = create<ConnectionState>((set) => ({
  status: 'reconnecting',
  lastReconnectAt: 0,
  socketId: null,
  setStatus: (status) => set({ status }),
  bumpReconnect: () => set({ lastReconnectAt: Date.now() }),
  setSocketId: (socketId) => set({ socketId }),
}));

/**
 * Per-chat dedup for the reconnect catch-up. Returns true the first
 * time a given `lastReconnectAt` is seen for `chatKey`, false on
 * subsequent calls with same/older timestamp. Lets the chat page's
 * catch-up effect skip wasted GETs on plain re-mounts and fire only
 * when a real reconnect happened since the last time the chat
 * caught up. Module-level Map so dedup persists across mount/unmount.
 */
const lastCatchup = new Map<string, number>();
export function shouldCatchup(chatKey: string, ts: number): boolean {
  if (ts <= (lastCatchup.get(chatKey) ?? 0)) return false;
  lastCatchup.set(chatKey, ts);
  return true;
}
